import { create } from 'zustand';
import { condoApi, toCondoError, type Condo, type CondoError, type MyCondo, type Permission } from '../../../lib/api/condo';
import { pickDefaultRole, uiPermissions, type WebRole } from '../lib/permissions';

export const CONDO_CTX_KEY = 'synclyst.condo.ctx';
type Status = 'idle' | 'loading' | 'ready' | 'error';

interface PersistedCtx {
  condoId?: string | null;
  role?: string | null;
}

function readCtx(): PersistedCtx {
  try {
    return JSON.parse(localStorage.getItem(CONDO_CTX_KEY) || '{}') as PersistedCtx;
  } catch {
    return {};
  }
}

// Solo se guardan ids de seleccion (no datos sensibles).
function writeCtx(ctx: PersistedCtx) {
  try {
    localStorage.setItem(CONDO_CTX_KEY, JSON.stringify(ctx));
  } catch {
    /* almacenamiento no disponible */
  }
}

export interface CondoState {
  condos: MyCondo[];
  status: Status;
  error: CondoError | null;
  activeCondoId: string | null;
  activeRole: WebRole | null;
  condo: Condo | null;
  /** Efectivos (union de roles): mandan. */
  permissions: Permission[];
  byRole: Partial<Record<string, Permission[]>>;
  /** Efectivos filtrados por by_role[activeRole]: solo presentacion. */
  uiPermissions: Permission[];
  permStatus: Status;
  loadCondos: (preferredCondoId?: string | null) => Promise<void>;
  selectCondo: (condoId: string) => Promise<void>;
  selectRole: (role: WebRole) => void;
  reset: () => void;
}

const initial = {
  condos: [] as MyCondo[],
  status: 'idle' as Status,
  error: null,
  activeCondoId: null,
  activeRole: null,
  condo: null,
  permissions: [] as Permission[],
  byRole: {} as Partial<Record<string, Permission[]>>,
  uiPermissions: [] as Permission[],
  permStatus: 'idle' as Status,
};

export const useCondoStore = create<CondoState>((set, get) => ({
  ...initial,

  async loadCondos(preferredCondoId) {
    set({ status: 'loading', error: null });
    try {
      // business_type del contrato activa el modulo: solo condominios hoa entran al contexto.
      const condos = (await condoApi.myCondos()).filter((c) => c.business_type === 'hoa');
      set({ condos, status: 'ready' });
      const saved = readCtx();
      const wanted = preferredCondoId || saved.condoId;
      const target = condos.find((c) => c.id === wanted) ?? condos[0];
      if (target?.id) await get().selectCondo(target.id);
    } catch (err) {
      set({ status: 'error', error: toCondoError(err) });
    }
  },

  async selectCondo(condoId) {
    const mine = get().condos.find((c) => c.id === condoId);
    const role = pickDefaultRole(mine?.roles, readCtx().role);
    set({ activeCondoId: condoId, activeRole: role, permissions: [], byRole: {}, uiPermissions: [], condo: null, permStatus: 'loading', error: null });
    writeCtx({ condoId, role });
    try {
      const [perms, condo] = await Promise.all([condoApi.myPermissions(condoId), condoApi.getCondo(condoId)]);
      if (get().activeCondoId !== condoId) return; // cambio de contexto en curso
      const roles = perms.roles ?? mine?.roles;
      const activeRole = pickDefaultRole(roles, role);
      const byRole = (perms.by_role ?? {}) as Partial<Record<string, Permission[]>>;
      set({
        condos: get().condos.map((c) => (c.id === condoId ? { ...c, roles } : c)),
        permissions: perms.permissions ?? [],
        byRole,
        uiPermissions: uiPermissions(perms.permissions, byRole, activeRole),
        activeRole,
        condo,
        permStatus: 'ready',
      });
    } catch (err) {
      if (get().activeCondoId !== condoId) return;
      set({ permissions: [], byRole: {}, uiPermissions: [], permStatus: 'error', error: toCondoError(err) });
    }
  },

  selectRole(role) {
    const mine = get().condos.find((c) => c.id === get().activeCondoId);
    if (!mine?.roles?.includes(role)) return;
    const { permissions, byRole } = get();
    set({ activeRole: role, uiPermissions: uiPermissions(permissions, byRole, role) });
    writeCtx({ condoId: get().activeCondoId, role });
  },

  reset() {
    set({ ...initial });
  },
}));
